import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Card } from '../../components/ui/Card';
import {
  User,
  Mail,
  CheckCircle2,
  Copy,
  Save,
} from 'lucide-react';

export const AccountGeneralTab: React.FC = () => {
  const { user, updateProfile } = useAuth();
  const { showToast } = useToast();

  const [fullName, setFullName] = useState(user?.full_name || '');
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [copiedUserId, setCopiedUserId] = useState(false);

  useEffect(() => {
    if (user) {
      setFullName(user.full_name || '');
    }
  }, [user]);

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fullName.trim()) return;

    setIsSavingProfile(true);
    try {
      await updateProfile({ full_name: fullName.trim() });
      showToast({
        type: 'success',
        title: 'Profile Updated',
        message: 'Your account details have been saved.',
      });
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Profile Update Failed',
        message: err?.message || 'Failed to update profile.',
      });
    } finally {
      setIsSavingProfile(false);
    }
  };

  const copyUserId = () => {
    if (user?.id) {
      navigator.clipboard.writeText(user.id);
      setCopiedUserId(true);
      setTimeout(() => setCopiedUserId(false), 2000);
      showToast({
        type: 'info',
        title: 'Copied',
        message: 'User ID copied to clipboard.',
      });
    }
  };

  return (
    <div className="space-y-6">
      <Card padding="lg" className="space-y-4">
        <h2 className="text-sm font-semibold text-cf-textPrimary flex items-center gap-2">
          <User size={16} className="text-cf-orange" />
          <span>Personal Profile</span>
        </h2>

        <form onSubmit={handleSaveProfile} className="space-y-4">
          <div className="flex items-center gap-3 p-3 bg-cf-bg border border-cf-border rounded-lg">
            <div className="w-12 h-12 rounded-full bg-gradient-to-tr from-cf-orange to-amber-500 text-white font-bold flex items-center justify-center text-base shadow-sm flex-shrink-0">
              {fullName ? fullName.substring(0, 2).toUpperCase() : user?.email?.substring(0, 2).toUpperCase() || 'ER'}
            </div>
            <div className="min-w-0">
              <div className="font-semibold text-cf-textPrimary text-sm truncate">
                {user?.full_name || 'Ericius User'}
              </div>
              <div className="text-cf-textSecondary text-xs truncate flex items-center gap-1.5 mt-0.5">
                <Mail size={12} className="text-cf-orange" />
                <span>{user?.email}</span>
                <span className="inline-flex items-center gap-0.5 text-xs text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded border border-emerald-500/20">
                  <CheckCircle2 size={10} /> Verified
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Display Name"
              type="text"
              required
              size="sm"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="e.g. Alex Johnson"
            />

            <Input
              label="Primary Email"
              type="email"
              disabled
              size="sm"
              value={user?.email || ''}
            />
          </div>

          <Input
            label="User UUID"
            type="text"
            readOnly
            size="sm"
            mono
            value={user?.id || ''}
            rightAction={
              <Button
                type="button"
                variant="ghost"
                size="xs"
                onClick={copyUserId}
                icon={copiedUserId ? <CheckCircle2 size={13} className="text-emerald-400" /> : <Copy size={13} />}
                className="h-auto py-0 px-1"
              >
                {copiedUserId ? 'Copied' : 'Copy'}
              </Button>
            }
          />

          <div className="flex justify-end pt-2">
            <Button
              type="submit"
              variant="primary"
              size="xs"
              disabled={isSavingProfile || !fullName.trim()}
              loading={isSavingProfile}
              icon={<Save size={13} />}
            >
              Save Profile
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};
